import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Quote, Download, Mail, Newspaper } from "lucide-react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserPressService } from "../services/UserPressServices";
import { UserBiographyServices } from "../services/BiographyServices";

import { UserGalleryService } from "../services/UserGalleryServices";
import { useNotification } from "../context/NotificationContext";

export default function Press() {
    const [pressItems, setPressItems] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [epk, setEpk] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingBio, setDownloadingBio] = useState(false);
    const { showNotification } = useNotification();

    useEffect(() => {
        const fetchPressData = async () => {
            try {
                const [coverageData, quotesData, epkData] = await Promise.all([
                    UserPressService.getPressCoverage(),
                    UserPressService.getPressQuotes(),
                    UserPressService.getEPK()
                ]);
                setPressItems(coverageData);
                setQuotes(quotesData);
                if (epkData && epkData.status === "success") {
                    setEpk(epkData.data);
                }
            } catch (err) {
                console.error("Failed to fetch press data:", err);
                showNotification({
                    message: "Unable to load press information at this time.",
                    type: "error"
                });
                setError("Unable to load press information at this time.");
            } finally {
                setLoading(false);
            }
        };
        fetchPressData();
    }, [showNotification]);

    const handleDownloadPhotos = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            await UserGalleryService.downloadPhotos();
            showNotification({ message: "Download started successfully", type: "success" });
        } catch (err) {
            console.error("Download failed:", err);

            // Extract message from server if available
            let errorMessage = "Failed to download press photos. Please try again later.";
            if (err.response && err.response.data) {
                // Handle blob response if it's actually JSON (happens in errors)
                if (err.response.data instanceof Blob) {
                    const text = await err.response.data.text();
                    try {
                        const jsonData = JSON.parse(text);
                        errorMessage = jsonData.message || errorMessage;
                    } catch (e) {
                        // Not JSON, use default
                    }
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            }

            showNotification({
                message: errorMessage,
                type: err.response?.status === 404 ? "info" : "error"
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadEPK = () => {
        if (epk && epk.file_path) {
            window.open(epk.file_path, "_blank");
            showNotification({ message: "EPK download started", type: "success" });
        } else {
            showNotification({
                message: "Electronic Press Kit (EPK) is currently unavailable.",
                type: "info"
            });
        }
    };

    const handleDownloadBio = async () => {
        if (downloadingBio) return;
        setDownloadingBio(true);
        try {
            await UserBiographyServices.downloadBio();
            showNotification({ message: "Biography download started", type: "success" });
        } catch (err) {
            console.error("Bio download failed:", err);

            let errorMessage = "Failed to download biography. Please try again later.";

            if (err.response && err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const jsonData = JSON.parse(text);
                    errorMessage = jsonData.message || errorMessage;
                } catch (e) {
                    // Not a JSON error, keep default
                }
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            showNotification({
                message: errorMessage,
                type: err.response?.status === 404 ? "info" : "error"
            });
        } finally {
            setDownloadingBio(false);
        }
    };

    const QuoteSkeleton = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border-l-4 border-gray-200 p-8 shadow-sm animate-pulse">
                    <div className="h-8 w-8 bg-gray-200 mb-4 rounded" />
                    <div className="space-y-3 mb-6">
                        <div className="h-4 bg-gray-200 rounded w-full" />
                        <div className="h-4 bg-gray-200 rounded w-5/6" />
                        <div className="h-4 bg-gray-200 rounded w-4/6" />
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
            ))}
        </div>
    );

    const CoverageSkeleton = () => (
        <div className="space-y-6 mb-24 max-w-5xl mx-auto">
            {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-black/10 p-8 animate-pulse shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                        <div className="md:col-span-2">
                            <div className="w-24 h-24 bg-gray-200 rounded" />
                        </div>
                        <div className="md:col-span-8 space-y-4">
                            <div className="h-3 bg-gray-200 rounded w-32" />
                            <div className="h-6 bg-gray-200 rounded w-3/4" />
                            <div className="h-4 bg-gray-200 rounded w-full" />
                        </div>
                        <div className="md:col-span-2">
                            <div className="h-4 bg-gray-200 rounded w-20 ml-auto" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-[#fafafa]">
            <SEO title="Press Coverage & Reviews" description="Read press coverage, reviews, articles, and access official press kit materials for Temidayo Balogun." url="/press" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6">

                    {/* Hero / Title Section */}
                    <div className="mb-16 sm:mb-20 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Press & Reviews
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />
                        <p
                            className="max-w-2xl mx-auto text-black font-medium text-lg leading-relaxed font-serif italic"
                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                            What critics, peers, and publications are saying about the music.
                        </p>
                    </div>

                    {/* Content Section */}
                    <div className="min-h-[800px]">
                        {loading ? (
                            <>
                                <QuoteSkeleton />
                                <div className="h-10 w-48 bg-gray-100 mx-auto mb-8 rounded animate-pulse" />
                                <CoverageSkeleton />
                            </>
                        ) : error ? (
                            <div className="text-center py-20 px-6 bg-white rounded-[2rem] border border-dashed border-black/20">
                                <Newspaper size={48} className="mx-auto text-black/40 mb-4" />
                                <p className="text-black font-serif italic text-lg">{error}</p>
                            </div>
                        ) : (
                            <>
                                {/* Featured Quotes Grid */}
                                {quotes.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                                        {quotes.map((quote, index) => (
                                            <div
                                                key={quote.id || index}
                                                className="bg-white border-l-4 border-jazz-brown p-8 shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] transition-shadow duration-300"
                                            >
                                                <Quote size={32} className="text-jazz-brown/50 mb-4" />
                                                <p
                                                    className="text-lg text-black font-medium mb-6 leading-relaxed"
                                                    style={{
                                                        fontFamily: "Crimson Pro, Georgia, serif",
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    "{quote.text}"
                                                </p>
                                                <p className="text-sm font-bold text-black uppercase tracking-wider">
                                                    — {quote.source}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Press Archive List */}
                                <div className="space-y-6 mb-24 max-w-5xl mx-auto">
                                    <h2 className="text-2xl font-light mb-8 text-center" style={{ fontFamily: "Playfair Display, serif" }}>Latest Coverage</h2>
                                    {pressItems.length > 0 ? (
                                        pressItems.map((item, index) => (
                                            <article
                                                key={item.id || index}
                                                className="group bg-white border border-black/10 hover:border-jazz-brown/50 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-6 sm:p-8"
                                            >
                                                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                                                    {/* Logo */}
                                                    <div className="md:col-span-2">
                                                        <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-50 border border-black/5 flex items-center justify-center p-2">
                                                            {item.logo_path ? (
                                                                <img
                                                                    src={item.logo_path}
                                                                    alt={item.publication}
                                                                    className="max-w-full max-h-full object-contain grayscale opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                                                                />
                                                            ) : (
                                                                <Newspaper className="text-black/20" size={32} />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Content */}
                                                    <div className="md:col-span-8 space-y-2">
                                                        <div className="flex items-center gap-3 text-xs font-bold tracking-widest text-black/80 uppercase">
                                                            <span className="text-jazz-brown">{item.type}</span>
                                                            <span>•</span>
                                                            <span>{item.date_display}</span>
                                                        </div>
                                                        <h3
                                                            className="text-xl sm:text-2xl font-medium text-black group-hover:text-jazz-brown transition-colors duration-300"
                                                            style={{ fontFamily: "Playfair Display, serif" }}
                                                        >
                                                            {item.headline}
                                                        </h3>
                                                        <p className="text-black font-medium font-serif leading-relaxed line-clamp-2">
                                                            {item.excerpt}
                                                        </p>
                                                    </div>

                                                    {/* Action */}
                                                    <div className="md:col-span-2 text-right">
                                                        <a
                                                            href={item.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-black hover:text-jazz-brown transition-colors border-b border-black/20 hover:border-jazz-brown pb-1"
                                                        >
                                                            READ FULL <ExternalLink size={12} />
                                                        </a>
                                                    </div>
                                                </div>
                                            </article>
                                        ))
                                    ) : (
                                        <div className="text-center py-20 border border-dashed border-black/20 rounded-[2rem] bg-white">
                                            <Newspaper size={48} className="mx-auto text-black/40 mb-4" />
                                            <p className="text-black font-serif italic text-lg">No recent coverage found.</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Media Kit Section */}
                    <div className="bg-white border border-black/10 p-8 sm:p-16 text-center">
                        <h2 className="text-3xl font-light mb-6" style={{ fontFamily: "Playfair Display, serif" }}>Media Kit</h2>
                        <p className="text-black/80 max-w-2xl mx-auto mb-10 leading-relaxed font-serif font-medium">
                            Access high-resolution press photos, official bio, logos, and technical riders for venue and press usage.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <button
                                onClick={handleDownloadPhotos}
                                disabled={downloading}
                                className="flex items-center gap-2 px-6 py-3 bg-black text-white text-xs font-bold tracking-widest hover:bg-jazz-brown transition-colors disabled:opacity-50"
                            >
                                <Download size={16} /> {downloading ? "DOWNLOADING..." : "DOWNLOAD PHOTOS"}
                            </button>
                            <button
                                onClick={handleDownloadEPK}
                                className="flex items-center gap-2 px-6 py-3 border border-black text-black text-xs font-bold tracking-widest hover:bg-gray-50 transition-colors"
                            >
                                <Download size={16} /> DOWNLOAD EPK
                            </button>
                            <button
                                onClick={handleDownloadBio}
                                disabled={downloadingBio}
                                className="flex items-center gap-2 px-6 py-3 border border-black text-black text-xs font-bold tracking-widest hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                <Download size={16} /> {downloadingBio ? "DOWNLOADING..." : "DOWNLOAD BIO"}
                            </button>
                        </div>

                        <div className="mt-12 pt-8 border-t border-black/10">
                            <p className="text-sm text-black/80 mb-2 font-bold tracking-wider">PRESS INQUIRIES</p>
                            <Link to="mailto:press@temidayobalogun.com" className="text-xl font-bold hover:text-jazz-brown transition-colors flex items-center justify-center gap-2">
                                <Mail size={20} /> press@temidayobalogun.com
                            </Link>
                        </div>
                    </div>

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
